import React from "react";
import CustomModalV1 from "components/custom-modal/CustomModalV1";
import AuthForm from "components/auth-form/AuthForm";
import Button from "components/button/Button";
import { XIconV1 } from "images/image-component";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import { useSuspendCard } from "./useSuspendCard";
import "./suspend-card-modal.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const TOTAL_STEPS = 2;

/**
 * Suspend the cardholder's own card: password, then an explicit confirmation of what it costs.
 *
 * No "try again" on the error modal — a modal-driven resubmit would spend one of the three
 * password attempts without the user choosing to.
 */
function SuspendCardModal({ isOpen, onClose }: Props) {
  const {
    step,
    title,
    subtitle,
    fields,
    submitRef,
    submitLabel,
    isSubmitDisabled,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    showSuccess,
    successMsg,
    endsSession,
    handleSubmit,
    handleBack,
    handleConfirmSuspend,
    handleCloseError,
    handleCloseSuccess,
    reset,
  } = useSuspendCard();

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSuccessClose = () => {
    handleCloseSuccess();
    reset();
    onClose();
  };

  // The last wrong password ends the session on close, and reset() is what fires it — so that
  // close has to take the whole modal down, not just the error over it.
  const handleErrorClose = () => {
    if (endsSession) return handleClose();
    handleCloseError();
  };

  return (
    <>
      <CustomModalV1 isOpen={isOpen} handleClose={handleClose}>
        <button
          className="v1-modal-close"
          aria-label="Close"
          onClick={handleClose}
          disabled={showLoading}
        >
          <XIconV1 />
        </button>

        <div className="scm-header">
          <span className="scm-step">
            Step {step} of {TOTAL_STEPS}
          </span>
          <h2 className="v1-modal-title">{title}</h2>
          <p className="v1-modal-subtitle">{subtitle}</p>
        </div>

        {step === 1 && (
          <>
            <AuthForm
              fields={fields}
              submitLabel={submitLabel}
              onSubmit={handleSubmit}
              disabled={isSubmitDisabled}
              isLoading={showLoading}
              submitRef={submitRef}
            />
            <div className="v1-modal-actions">
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={showLoading}
              >
                Cancel
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <ul className="scm-warning">
              <li>
                Your card stops working immediately. Payments and refunds will
                be declined.
              </li>
              <li>
                You can't undo this yourself — contact support to reactivate the
                card.
              </li>
              <li>Your balance is not affected and stays on the card.</li>
            </ul>
            <div className="v1-modal-actions">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={showLoading}
              >
                Back
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmSuspend}
                disabled={isSubmitDisabled || showLoading}
              >
                {submitLabel}
              </Button>
            </div>
          </>
        )}
      </CustomModalV1>

      <LoadingModal isOpen={showLoading} loadingMsg={loadingMsg} />

      <ErrorModal
        isOpen={showError}
        errorMsg={errorMsg}
        handleClose={handleErrorClose}
      />

      <SuccessModal
        isOpen={showSuccess}
        message={successMsg}
        handleClose={handleSuccessClose}
      />
    </>
  );
}

export default SuspendCardModal;
